import { useState } from "react";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { supplierBasicPermissions } from "../config";
import { useSupplierBasicRuntime } from "../runtime/useSupplierBasicRuntime";
import type { SupplierItem, SupplierKind } from "../types";
import { SupplierDetailDrawer } from "./SupplierDetailDrawer";
import { SupplyBasicFilterPanel } from "./SupplyBasicFilterPanel";
import { SupplyBasicMutationModal } from "./SupplyBasicMutationModal";
import { SupplyBasicResultTable } from "./SupplyBasicResultTable";
import "./SupplyBasicPage.css";

type ModalState = { type: "create" } | { type: "edit"; item: SupplierItem };

type SupplyBasicFeaturePageProps = {
  kind: SupplierKind;
};

/** 供应链基础设置共用列表页。 */
export function SupplyBasicFeaturePage({ kind }: SupplyBasicFeaturePageProps) {
  const runtime = useSupplierBasicRuntime(kind);
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, supplierBasicPermissions.manage);
  const canManageContact = canUsePermission(
    currentUser,
    supplierBasicPermissions.manageContact,
  );
  const canManageAccount = canUsePermission(
    currentUser,
    supplierBasicPermissions.manageAccount,
  );
  const canManageAttachment = canUsePermission(
    currentUser,
    supplierBasicPermissions.manageAttachment,
  );
  const canManageCapability = canUsePermission(
    currentUser,
    supplierBasicPermissions.manageCapability,
  );
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [detailItem, setDetailItem] = useState<SupplierItem | null>(null);

  return (
    <main className="supply-basic-page">
      <SupplyBasicFilterPanel
        title={runtime.meta.title}
        filters={runtime.draftFilters}
        errorMessage={runtime.errorMessage}
        onChange={runtime.setDraftFilters}
        onReset={runtime.handleReset}
        onSearch={runtime.handleSearch}
      />
      <SupplyBasicResultTable
        kind={kind}
        meta={runtime.meta}
        items={runtime.items}
        loading={runtime.loading}
        page={runtime.page}
        pageSize={runtime.pageSize}
        total={runtime.total}
        canManage={canManage}
        onCreate={() => setModalState({ type: "create" })}
        onEdit={(item) => setModalState({ type: "edit", item })}
        onViewDetails={setDetailItem}
        onDisable={(item) => void runtime.handleDisable(item)}
        onEnable={(item) => void runtime.handleEnable(item)}
        onPageChange={runtime.handlePageChange}
        onPageSizeChange={runtime.handlePageSizeChange}
      />
      <SupplyBasicMutationModal
        kind={kind}
        meta={runtime.meta}
        state={modalState}
        onClose={() => setModalState(null)}
        errorMessage={runtime.mutationErrorMessage}
        groundOptions={runtime.groundOptions}
        userOptions={runtime.userOptions}
        onSearchUserOptions={runtime.searchUserOptions}
        onSearchGroundOptions={runtime.searchGroundOptions}
        onCreate={runtime.handleCreate}
        onUpdate={runtime.handleUpdate}
        onClearError={runtime.clearMutationError}
      />
      {detailItem ? (
        <SupplierDetailDrawer
          kind={kind}
          item={detailItem}
          canManageContact={canManageContact}
          canManageAccount={canManageAccount}
          canManageAttachment={canManageAttachment}
          canManageCapability={canManageCapability}
          onClose={() => setDetailItem(null)}
        />
      ) : null}
    </main>
  );
}
